<template>
  <!-- One layer row (boards Session and MacMain): the owner's dot, the
       waveform in their session colour at true level, play, and •••.
       Star, download, rename, the owner's profile, the loop's link and
       details, and report live in the ••• menu (MacContext), which a
       right-click opens too. While the session plays (Play all) the row
       shows where this loop is in the mix. -->
  <div
    class="layer-row"
    :class="{
      'is-active': isActive,
      'is-reservation': isReservation,
      'is-menu-open': menuOpen,
    }"
    :style="{ '--row-color': rowColor }"
    :draggable="draggable ? 'true' : null"
    @contextmenu="onContextMenu"
    @dragstart="onDragStart"
  >
    <PersonDot :name="ownerLabel" :color="rowColor" :size="tabletRow ? 36 : 28" class="layer-dot" />

    <div class="layer-body">
      <div class="layer-label-row">
        <input
          v-if="isEditing"
          ref="nameInput"
          v-model="loopName"
          class="layer-name-input"
          maxlength="80"
          aria-label="Loop name"
          @keyup.enter="saveName"
          @keyup.esc="cancelEdit"
          @blur="saveName"
        />
        <span v-else class="layer-name" :title="loopName"
          >{{ loopName
          }}<span v-if="loopKey" class="layer-key"> · {{ loopKey }}</span></span
        >
        <!-- A sound you'd have to buy: the price chip (plan section 1),
             which opens the buy sheet (signed out, sign-up first); rights
             reserved with no price is "Not for sale" and never buyable
             (decision 15). -->
        <button
          v-if="forSale && !isEditing"
          type="button"
          class="layer-price"
          :aria-label="`Buy ${loopName}, from ${priceText}`"
          @click="openBuy"
        >
          <SaleChip kind="price" :label="priceText" />
        </button>
        <SaleChip
          v-else-if="saleState === 'not_for_sale' && !isEditing"
          class="layer-sale"
          kind="muted"
          label="Not for sale"
        />
      </div>
      <div class="layer-wave">
        <div class="wave-bars">
          <WaveMask
            v-if="!isReservation"
            :hash="loop.hash"
            :progress="shownPercent"
            :loaded="loadedPercent"
            seekable
            @seek="onSeek"
          />
        </div>
        <span
          v-if="isActive"
          class="layer-playhead"
          :style="{ left: shownPercent + '%' }"
        />
      </div>
    </div>

    <button
      v-if="!isReservation"
      type="button"
      class="layer-play"
      :aria-label="isPlaying ? `Pause ${loopName}` : `Play ${loopName}`"
      @click="playPause"
    >
      <v-progress-circular
        v-if="isBuffering"
        indeterminate
        size="16"
        width="2"
      />
      <v-icon v-else class="layer-play-icon">{{
        isPlaying ? "mdi-pause" : "mdi-play"
      }}</v-icon>
    </button>

    <span class="layer-more">
      <MoreMenu
        ref="more"
        :items="menuItems"
        :caption="menuCaption"
        :width="236"
        label="Loop actions"
        :size="tabletRow ? 44 : 28"
        @update:open="onMenuOpen"
      />
      <!-- Loop details: placed at the ••• and opened from its menu. A
           target, not an activator, so the ••• keeps its own keys (ArrowDown
           opens only the loop menu) and its own aria. -->
      <v-menu
        v-model="infoOpen"
        target="parent"
        :capture-focus="false"
        location="bottom end"
        offset="6"
      >
        <div class="loop-info">
          <div class="loop-info-title">{{ loopName }}</div>
          <dl class="loop-info-list">
            <div class="loop-info-row">
              <dt>By</dt>
              <dd>
                <a class="loop-info-link" @click="goToOwner">{{ ownerLabel }}</a>
                <span v-if="ownerLabel !== loop.creator" class="loop-info-handle">
                  @{{ loop.creator }}
                </span>
              </dd>
            </div>
            <div class="loop-info-row">
              <dt>Added</dt>
              <dd>{{ formatDate(loop.created_at) }}</dd>
            </div>
            <div class="loop-info-row">
              <dt>ID</dt>
              <dd class="loop-info-id">{{ loop.hash.substring(0, 8) }}</dd>
            </div>
          </dl>
        </div>
      </v-menu>
    </span>

    <ReportDialog
      v-if="canReport"
      v-model:show="showReport"
      target-type="loop"
      :target-id="loop.hash"
      :target-label="loopName"
    />
    <PurchaseDialog
      v-model:show="showPurchaseDialog"
      :loop-hash="loop.hash"
      :loop-name="loopName"
      :seller-username="loop.creator"
      :min-price="checkoutMin"
      :suggested-price="suggestedPrice"
      @licensed="onLicensed"
    />
  </div>
</template>

<script>
import cloudloop from "@/mixins/cloudloop";
import audition from "@/audio/audition.js";
import PurchaseDialog from "@/components/PurchaseDialog.vue";
import ReportDialog from "@/components/ReportDialog.vue";
import WaveMask from "@/components/WaveMask.vue";
import PersonDot from "@/components/kit/PersonDot.vue";
import MoreMenu from "@/components/kit/MoreMenu.vue";
import SaleChip from "@/components/kit/SaleChip.vue";
import player from "@/audio/player";
import { loopMeta, loadLoopMeta, formatLength } from "@/audio/loopMeta";
import { forName } from "@/kit/people.js";
import { chipPriceUsd, loopOffer, saleTerms, usd } from "@/kit/sale.js";
import { getWavDownloadFromProxyWithId } from "@/filters/utils";
import { toSound } from "@/kit/sound.js";
import { onDragStart } from "@/services/soundDrag.js";

// A menu closing hands focus back to its button; anything it opens next
// (the rename field, the details popover) waits until that has happened.
const AFTER_MENU_MS = 120;

export default {
  name: "WaveformPlayer",
  // audio/audition.js owns the one streaming <audio>, progress and loaded
  // state and the Safari format fallback (SI-4); this component is the row
  // chrome. A row starting pauses the session's Play all (nowPlaying).
  mixins: [cloudloop],
  // Where the session sits in the signed-in shell (AppShell); null signed
  // out. A full-width session at touch draws TabletSession's 88 px rows.
  inject: { shellLayout: { default: null } },
  components: { PurchaseDialog, ReportDialog, WaveMask, PersonDot, MoreMenu, SaleChip },
  props: {
    loop: { type: Object, required: true },
    isLooping: { type: Boolean, default: false },
    // The owner's session colour (people.assign); a stable per-name pick
    // when the session doesn't supply one.
    color: { type: String, default: null },
    // The session page's colour for a creator (colorOf), so the bar keeps
    // each row's colour as ⏮ ⏭ step through the layers.
    colorFor: { type: Function, default: null },
    // The owner's display name, else their username.
    ownerName: { type: String, default: null },
  },
  // toast: "Link copied" and the like, for the page to show.
  // licensed: a $0 claim made this loop yours; the page refreshes its
  // download tokens.
  // sign-up: a signed-out visitor asked to buy; the page sends them to
  // create an account and brings them back.
  emits: ["toast", "licensed", "sign-up"],
  data() {
    return {
      isReservation: false,
      loopName: "",
      isEditing: false,
      showPurchaseDialog: false,
      showReport: false,
      menuOpen: false,
      infoOpen: false,
      removing: false,
      accessInfo: null,
    };
  },
  computed: {
    tabletRow() {
      const l = this.shellLayout ? this.shellLayout.value || this.shellLayout : null;
      return !!l && !!l.inShell && l.mode === "full" && l.density === "touch";
    },
    // This row's sound in the shared preview engine.
    isPlaying() {
      return audition.isPlaying(this.loop.hash);
    },
    isBuffering() {
      return audition.isBuffering(this.loop.hash);
    },
    progressPercent() {
      return audition.progressOf(this.loop.hash);
    },
    loadedPercent() {
      return audition.loadedOf(this.loop.hash);
    },
    // The row as a kit/sound.js Sound: its tokens from the session's
    // media-urls answer, its terms from /access (signed in) or that answer.
    sound() {
      return this.soundFor(this.loop, this.access);
    },
    // Free or owned: it drags out to a DAW or the Finder (d45, SI-11).
    // From anywhere on the row at pointer density; at touch the row
    // scrolls, so it doesn't drag.
    draggable() {
      // Signed in only: the guest frame's session page stays as P1 built it.
      if (!this.me || this.isReservation || this.isEditing) return false;
      if (typeof document !== "undefined" && document.documentElement.dataset.density === "touch") return false;
      return this.sound.isFree || this.sound.isOwned;
    },
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    rowColor() {
      return this.color || forName(this.loop.creator, this.me);
    },
    ownerLabel() {
      return this.ownerName || this.loop.creator || "";
    },
    ownerFirst() {
      const first = (this.ownerLabel || "").trim().split(/\s+/)[0];
      return first || this.loop.creator;
    },
    // Mirrors the server rule: a session member who uploaded the loop, or
    // the session owner (who curates, e.g. prunes a fork's inherited loops).
    canRemove() {
      const me = this.me;
      const session = this.$store.state.selectedSession;
      if (!me || !session || !(session.users || []).includes(me)) return false;
      return this.loop.creator === me || session.creator === me;
    },
    isStarred() {
      return this.$store.getters.isLoopStarred(this.loop.hash);
    },
    // Where this loop is in the session's mix while Play all holds this
    // session (playing or paused), else null.
    mixPercent() {
      if (this.isReservation) return null;
      const name = this.$store.state.selectedSession?.name;
      return name ? player.layerProgress(name, this.loop.hash) : null;
    },
    // Played on its own (this row's play button).
    soloActive() {
      return this.isPlaying || this.progressPercent > 0;
    },
    // Played or playing, on its own or in the mix: the wave splits into
    // played (full colour) and the rest (36%) with a playhead, as the
    // boards draw a sounding layer.
    isActive() {
      return this.soloActive || this.mixPercent != null;
    },
    shownPercent() {
      if (this.soloActive) return this.progressPercent;
      return this.mixPercent ?? 0;
    },
    // MacContext's caption: "Maya's loop · F♯m · 4.0 s".
    menuCaption() {
      const meta = loopMeta(this.loop.hash);
      const parts = [this.loop.creator === this.me ? "Your loop" : `${this.ownerFirst}'s loop`];
      if (meta?.key) parts.push(meta.key);
      if (meta?.duration) parts.push(formatLength(meta.duration));
      return parts.join(" · ");
    },
    // Anyone signed in may report someone else's loop.
    canReport() {
      return !!this.me && this.loop.creator !== this.me && !this.isReservation;
    },
    // The loop's sale terms and access. Signed in: its /access answer.
    // Signed out: its entry in the session's /media/session-urls answer,
    // which carries the same terms (pricing.terms_fields), so a visitor
    // sees the price and Buy, never a download that can't work.
    access() {
      if (this.me) return this.accessInfo;
      // Only a media-urls entry says what a loop costs (another page's
      // token map, e.g. a pack's, has no terms: unknown until it lands).
      const entry = this.$store.state.mediaTokens?.loops?.[this.loop.hash];
      return entry && "accessible" in entry ? entry : null;
    },
    offer() {
      return loopOffer(this.access, { signedIn: !!this.me });
    },
    needsPurchase() {
      return this.offer.needsPurchase;
    },
    // for_sale, not_for_sale or custom; an older server without
    // license_state: worked out from the prices (loopOffer).
    saleState() {
      return this.offer.saleState;
    },
    forSale() {
      return this.saleState === "for_sale";
    },
    minPrice() {
      return Number(this.access?.min_price_usd || 0);
    },
    // The least a checkout takes ("$3.00").
    checkoutMin() {
      const a = this.access || {};
      if (a.checkout_min_usd != null) return Number(a.checkout_min_usd);
      return saleTerms(a).checkoutMin ?? this.minPrice;
    },
    // The chip, as the app's sound rows and the pack page's rows price a
    // sound (chipPriceUsd): what it costs at least. The sheet it opens
    // starts at the suggested price, as the app's BuySheet does.
    priceText() {
      return usd(chipPriceUsd({ checkoutMin: this.checkoutMin, suggested: this.suggestedPrice }));
    },
    // The row label's key ("Brushed kit · Dm"), once /api/loop answers.
    loopKey() {
      return loopMeta(this.loop.hash)?.key || "";
    },
    // Only the loop's creator may rename it (the server refuses others).
    canRename() {
      return !!this.me && this.loop.creator === this.me && !this.isReservation;
    },
    suggestedPrice() {
      return this.access?.suggested_price_usd != null
        ? Number(this.access.suggested_price_usd)
        : null;
    },
    // MacContext's order, without the looper's rows (WEB §2): the loop's
    // own actions, Download, then the owner's profile and the link, then
    // Report. The web adds Star, Rename, Loop info and Remove.
    menuItems() {
      const items = [];
      if (this.me) {
        items.push({
          title: this.isStarred ? "Unstar" : "Star",
          icon: this.isStarred ? "mdi-star" : "mdi-star-outline",
          onClick: this.toggleStar,
        });
        if (this.canRename) {
          items.push({ title: "Rename", icon: "mdi-pencil-outline", onClick: this.startEditSoon });
        }
        items.push({ divider: true });
      }
      if (!this.isReservation) {
        // Decision 25: a sound you'd have to buy shows Buy, not Download;
        // signed out, Buy… asks for an account first. "Buy…" with the
        // cart, as on the pack page's rows (the app's sound menu).
        let row = null;
        if (this.offer.canDownload) {
          row = { title: "Download loop (.wav)", icon: "mdi-download", onClick: this.downloadLoop };
        } else if (this.saleState === "not_for_sale") {
          row = { title: "Not for sale", icon: "mdi-lock-outline", disabled: true, onClick: () => {} };
        } else if (this.needsPurchase) {
          row = { title: "Buy…", icon: "mdi-cart-outline", onClick: this.openBuy };
        }
        if (row) items.push(row, { divider: true });
      }
      items.push({
        title: this.loop.creator === this.me ? "Show your profile" : `Show ${this.ownerFirst}'s profile`,
        icon: "mdi-account-outline",
        onClick: this.goToOwner,
      });
      if (!this.isReservation) {
        items.push({ title: "Copy loop link", icon: "mdi-link-variant", onClick: this.copyLoopLink });
      }
      items.push({ title: "Loop info", icon: "mdi-information-outline", onClick: this.openInfoSoon });
      if (this.canRemove || this.canReport) items.push({ divider: true });
      if (this.canRemove) {
        items.push({
          title: "Remove from session",
          icon: "mdi-trash-can-outline",
          danger: true,
          disabled: this.removing,
          onClick: this.removeFromSession,
        });
      }
      if (this.canReport) {
        items.push({
          title: "Report loop…",
          icon: "mdi-flag-outline",
          dim: true,
          onClick: () => (this.showReport = true),
        });
      }
      return items;
    },
  },
  watch: {
    // Reflect a rename that arrives via state_update (from this client's save
    // or a collaborator's) — unless the user is mid-edit in this row.
    "loop.name"(newName) {
      if (!this.isEditing) {
        this.loopName = newName || `Loop ${this.loop.hash.substring(0, 6)}`;
      }
    },
    isLooping(v) {
      if (audition.isCurrent(this.loop.hash)) audition.setLoop(v);
    },
  },
  mounted() {
    const { loop } = this.$props;
    this.loopName = loop.name || `Loop ${loop.hash.substring(0, 6)}`;
    this.isReservation = (loop.link || "").substring(0, 10) === "reserve://";

    // No audio is loaded here — the waveform SVG mask paints immediately and
    // the <audio> element is created on the first play (audio/audition.js).
    if (!this.isReservation) {
      this.loadAccessInfo();
      // The key for the row label.
      loadLoopMeta([loop.hash]);
    }
  },

  methods: {
    onMenuOpen(open) {
      this.menuOpen = open;
      // The caption's key and length.
      if (open && !this.isReservation) loadLoopMeta([this.loop.hash]);
    },
    // A right-click opens the loop menu at the pointer (MacContext).
    onContextMenu(e) {
      if (this.isEditing || !this.$refs.more) return;
      e.preventDefault();
      this.$refs.more.openAt(e.clientX, e.clientY);
    },
    // Copy loop link (decision 25): the server's /l/<slug> link, never a
    // made-up URL. The clipboard write starts inside the click so Safari
    // keeps the gesture while the link is fetched.
    async copyLoopLink() {
      const linkPromise = this.getLoopLink(this.loop.hash).then((r) => {
        if (!r || !r.url) throw new Error("no link");
        return r.url;
      });
      try {
        if (window.ClipboardItem && navigator.clipboard?.write) {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              "text/plain": linkPromise.then((url) => new Blob([url], { type: "text/plain" })),
            }),
          ]);
        } else {
          await navigator.clipboard.writeText(await linkPromise);
        }
        this.$emit("toast", "Link copied");
      } catch (e) {
        const gotLink = await linkPromise.then(() => true).catch(() => false);
        this.$emit("toast", gotLink ? "Couldn't copy the link." : "Couldn't get a link to that loop.");
      }
    },
    onSeek(frac) {
      if (audition.isCurrent(this.loop.hash)) audition.seek(frac);
    },

    // Removes the loop from this session only — the loop itself, its
    // licenses and purchases are untouched. The list updates from the room's
    // state_update.
    async removeFromSession() {
      const session = this.$store.state.selectedSession;
      if (!session || this.removing) return;
      if (!window.confirm(`Remove "${this.loopName}" from this session?`)) return;
      this.removing = true;
      try {
        await this.deleteLoopInSession(session.name, this.loop.hash);
      } catch (status) {
        console.error("Failed to remove loop", status);
        window.alert("Couldn't remove this loop. Try again.");
      } finally {
        this.removing = false;
      }
    },

    // A $0 claim from the buy sheet: ask again, so the row drops its price
    // and the menu offers Download once the page has the new token.
    onLicensed() {
      this.loadAccessInfo();
      this.$emit("licensed", this.loop.hash);
    },
    async loadAccessInfo() {
      const me = this.me;
      // Own loops are always accessible; skip the round-trip.
      if (!me || me === this.loop.creator) return;
      // One retry smooths a transient blip before we give up.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await this.checkLoopAccess(this.loop.hash);
          this.accessInfo = response?.data?.results || null;
          return;
        } catch (error) {
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 400));
            continue;
          }
          // Couldn't confirm access. Leave accessInfo null (non-committal): we
          // don't know the license, so we must NOT fabricate a purchase dialog
          // — a CC loop would wrongly show a $0 "License" prompt. The backend is
          // the real gate: a rights-reserved loop has no minted download token,
          // so a plain download attempt 403s rather than leaking, and a CC loop
          // downloads normally. Self-heals on the next session load.
          console.warn("Loop access check failed after retry:", error);
          this.accessInfo = null;
        }
      }
    },

    // The price chip and Buy…: the buy sheet, or, signed out, sign-up
    // with a way straight back (the page owns the route and its ?via=).
    openBuy() {
      if (!this.me) {
        this.$emit("sign-up");
        return;
      }
      this.showPurchaseDialog = true;
    },
    downloadLoop() {
      if (this.needsPurchase) {
        if (this.saleState === "not_for_sale") return;
        this.openBuy();
        return;
      }
      if (!this.offer.canDownload) return;
      const downloadToken = this.$store.getters.mediaDownloadToken(
        this.$props.loop.hash,
      );
      const downloadLink = getWavDownloadFromProxyWithId(
        this.$props.loop.hash,
        downloadToken,
      );
      location.href = downloadLink;
    },
    toggleStar() {
      const hash = this.loop.hash;
      const wasStarred = this.isStarred;
      // Optimistic: flip the shared store immediately, revert on failure.
      if (wasStarred) {
        this.$store.commit("removeStarredLoop", hash);
        this.unstarLoop(hash).catch(() => this.$store.commit("addStarredLoop", hash));
      } else {
        this.$store.commit("addStarredLoop", hash);
        this.starLoop(hash).catch(() => this.$store.commit("removeStarredLoop", hash));
      }
    },
    goToOwner() {
      this.infoOpen = false;
      this.$router.push(`/users/${this.loop.creator}`);
    },
    openInfoSoon() {
      setTimeout(() => (this.infoOpen = true), AFTER_MENU_MS);
    },
    // A Sound for any loop of the session (the queue next and prev step
    // through), with what the page knows of its tokens and terms.
    soundFor(loop, access = null) {
      const tokens = this.$store.state.mediaTokens?.loops?.[loop.hash] || {};
      const info = access || (tokens && "accessible" in tokens ? tokens : null);
      const names = this.$store.state.selectedSession?.member_display_names || {};
      const row = {
        hash: loop.hash,
        name: loop === this.loop ? this.loopName || loop.name : loop.name,
        creator: loop.creator,
        creator_display_name: loop === this.loop ? this.ownerName : names[loop.creator] || null,
        session: this.$store.state.selectedSession?.name || null,
        session_display_name: this.$store.state.selectedSession?.alt_name || null,
        created_at: loop.created_at,
        stream_token: this.$store.getters.mediaStreamToken(loop.hash),
        download_token: this.$store.getters.mediaDownloadToken(loop.hash),
      };
      if (info) {
        Object.assign(row, {
          license_type: info.license_type,
          license_state: info.license_state,
          for_sale: info.for_sale,
          is_free: info.is_free,
          checkout_min_usd: info.checkout_min_usd,
          min_price_usd: info.min_price_usd,
          suggested_price_usd: info.suggested_price_usd,
          is_owned: info.accessible === true && info.license_type === "rights-reserved",
        });
      }
      const loopMetaInfo = loopMeta(loop.hash);
      if (loopMetaInfo?.duration) row.duration = loopMetaInfo.duration;
      // The key the row's label shows, and the session's tempo, so the bar
      // and the MiniPlayer read "Dm · 92" as on Sounds.
      if (loopMetaInfo?.key) row.key = loopMetaInfo.key;
      const bpm = Number(this.$store.state.selectedSession?.tempo_bpm);
      if (bpm > 0) row.bpm = bpm;
      return toSound(row, { me: this.me });
    },
    // The session's rows are the queue (the bar's next and prev); a
    // playlist session doesn't loop a row (loop: isLooping, as before).
    // Upload placeholders (reserve://) aren't sounds yet: ⏮ ⏭ skip them,
    // and keep each row's colour (L5).
    play() {
      const library = this.$store.state.selectedSession?.library || [];
      const queue = library
        .filter((l) => l && l.hash && (l === this.loop || !String(l.link || "").startsWith("reserve://")))
        .map((l) => (l.hash === this.loop.hash ? this.sound : this.soundFor(l)));
      const colorFor = (s) => (s.hash === this.loop.hash ? this.rowColor : this.colorFor ? this.colorFor(s.creator) : null);
      audition.playSound(this.sound, { queue, loop: this.isLooping, color: this.rowColor, colorFor });
    },
    playPause() {
      if (audition.isCurrent(this.loop.hash)) audition.toggle();
      else this.play();
    },
    pause() {
      if (audition.isCurrent(this.loop.hash)) audition.pause();
    },
    playFromStart() {
      this.play();
      audition.seek(0);
    },
    // Drag the row out (d45, SI-11): free or owned loops, Chromium only.
    onDragStart(e) {
      if (!this.draggable) {
        e.preventDefault();
        return;
      }
      onDragStart(e, this.sound);
    },
    startEditSoon() {
      setTimeout(this.startEdit, AFTER_MENU_MS);
    },
    startEdit() {
      if (!this.canRename) return;
      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.nameInput;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    cancelEdit() {
      this.isEditing = false;
      this.loopName = this.loop.name || `Loop ${this.loop.hash.substring(0, 6)}`;
    },
    async saveName() {
      if (!this.isEditing) return;
      this.isEditing = false;
      const name = (this.loopName || "").trim();
      const saved = this.loop.name || `Loop ${this.loop.hash.substring(0, 6)}`;
      if (!name || name === saved) {
        this.loopName = saved;
        return;
      }
      const ok = await this.updateLoopName(this.loop.hash, name);
      if (!ok) {
        // Refused or offline: the row shows the real name again.
        if (!this.isEditing) this.loopName = this.loop.name || saved;
        this.$emit("toast", "Couldn't rename this loop. Try again.");
      }
    },
    // "Oct 24, 2026, 8:03 PM" in the viewer's time. The server's naive
    // timestamps are UTC.
    formatDate(dateStr) {
      if (!dateStr) return "";
      const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(dateStr) ? dateStr : `${dateStr}Z`;
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
/* Phone (Session board): 64 tall, 14 radius, dot 28, wave 50, button 40. */
.layer-row {
  height: 64px;
  border-radius: 14px;
  background: var(--cl-cell);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px 0 12px;
}

/* The row whose menu is open (MacContext): a 2 px violet ring. */
.layer-row.is-menu-open {
  box-shadow: inset 0 0 0 2px var(--cl-accent);
}

.layer-dot {
  flex-shrink: 0;
}

.layer-body {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 50px;
}

/* The board's label sits over the top of the wave, 11/500 at label-3. */
.layer-label-row {
  position: absolute;
  left: 0;
  right: 0;
  top: -1px;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;
}

.layer-name {
  font: 500 11px/14px var(--cl-font);
  color: var(--cl-label-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.layer-name-input {
  pointer-events: auto;
  font: 500 12px/18px var(--cl-font);
  color: var(--cl-label);
  background: var(--cl-cell-inner);
  border: 1px solid var(--cl-accent-chip-border);
  border-radius: var(--cl-r-sm);
  padding: 0 6px;
  outline: none;
  min-width: 0;
  width: 70%;
}

/* The price chip (kit SaleChip) after the label, a button onto the buy
   sheet. */
.layer-price {
  pointer-events: auto;
  border: none;
  background: none;
  padding: 0;
  margin-top: -3px;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 10px;
}
.layer-price:hover :deep(.sale-chip) {
  color: var(--cl-on-fill);
}
.layer-price:focus-visible {
  outline: 2px solid var(--cl-accent-text);
  outline-offset: 1px;
}
.layer-sale {
  margin-top: -3px;
}

.layer-wave {
  position: absolute;
  inset: 0;
}

/* The server's silhouette (WaveMask) cut into 2 px bars on a 4 px step, the
   boards' stroke. Masking the wrapper keeps the level true: only gaps are
   cut, nothing is rescaled. */
.wave-bars {
  position: absolute;
  inset: 0;
  -webkit-mask-image: repeating-linear-gradient(
    90deg,
    var(--cl-on-fill) 0 2px,
    transparent 2px 4px
  );
  mask-image: repeating-linear-gradient(
    90deg,
    var(--cl-on-fill) 0 2px,
    transparent 2px 4px
  );
}

.wave-bars :deep(.wave-mask) {
  height: 100% !important;
}

/* Idle: the whole layer in its colour. Played or playing: played part full,
   the rest at 36%, as the boards draw a sounding layer. */
.wave-bars :deep(.wave-base) {
  background: var(--row-color);
}
.is-active .wave-bars :deep(.wave-base) {
  opacity: 0.36;
}
.wave-bars :deep(.wave-loaded) {
  background: var(--row-color);
  opacity: 0.5;
}
.wave-bars :deep(.wave-played) {
  background: var(--row-color);
}

.layer-playhead {
  position: absolute;
  top: 4px;
  bottom: 2px;
  width: 2px;
  margin-left: -1px;
  border-radius: 1px;
  background: var(--cl-on-fill);
  pointer-events: none;
}

.layer-play {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  flex-shrink: 0;
  background: var(--cl-control-fill);
  color: var(--cl-label);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.layer-play:hover {
  background: var(--cl-control-fill-strong);
}

.layer-play-icon {
  font-size: 22px;
}

.layer-more {
  display: inline-flex;
  flex-shrink: 0;
}

.layer-more :deep(.more-btn) {
  background: transparent;
  color: var(--cl-label-2);
}

.layer-more :deep(.more-btn:hover) {
  background: var(--cl-hover);
  color: var(--cl-label);
}

.is-reservation .layer-name {
  font-style: italic;
}

/* Mac pane (MacMain): 76 tall, dot 28, wave 60, button 34. */
/* The wide layout: the session page measures its own width (SI-2;
   under 700 px of pane it is the phone layout), not the window. */
.is-wide .layer-row {
  height: 76px;
  gap: 12px;
  padding: 0 10px 0 12px;
}

.is-wide .layer-body {
  height: 60px;
}

.is-wide .layer-playhead {
  top: 6px;
  bottom: 4px;
}

.is-wide .layer-play {
  width: 34px;
  height: 34px;
}

.is-wide .layer-play-icon {
  font-size: 19px;
}

/* TabletSession (a full-width session at touch, in the shell): 88 tall,
   radius 16, dot 36, wave 68, a 44 px play disc and a 44 px •••. */
.is-full-touch .layer-row {
  height: 88px;
  border-radius: 16px;
  gap: 12px;
  padding: 0 10px 0 14px;
}

.is-full-touch .layer-body {
  height: 68px;
}

.is-full-touch .layer-label-row {
  top: -2px;
}

.is-full-touch .layer-name {
  font: 500 12px/15px var(--cl-font);
  color: var(--cl-label-45);
}

.is-full-touch .layer-playhead {
  top: 8px;
  bottom: 6px;
}

.is-full-touch .layer-play {
  width: 44px;
  height: 44px;
}

.is-full-touch .layer-play-icon {
  font-size: 22px;
}

.is-full-touch .layer-more :deep(.more-btn) {
  color: var(--cl-label-70);
}

/* The board's 20 px glyph (r 1.6 dots, 5.5 apart in 24): the icon font's
   dots at 18 px are the same 3 px dots 4.5 apart. The kit sets the icon's
   size inline, hence the !important. */
.is-full-touch .layer-more :deep(.more-btn .v-icon) {
  font-size: 18px !important;
  width: 18px !important;
  height: 18px !important;
}

/* Loop details popover (teleported, so not scoped to the row). */
.loop-info {
  min-width: 220px;
  max-width: 300px;
  padding: 10px 12px 12px;
  border-radius: var(--cl-r-lg);
  background: var(--cl-popover);
  border: 1px solid var(--cl-separator);
  box-shadow: 0 16px 40px var(--cl-scrim);
  color: var(--cl-label);
}

.loop-info-title {
  font: var(--cl-headline);
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loop-info-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.loop-info-row {
  display: flex;
  gap: 10px;
  font: var(--cl-callout);
}

.loop-info-row dt {
  width: 44px;
  flex-shrink: 0;
  color: var(--cl-label-3);
}

.loop-info-row dd {
  margin: 0;
  min-width: 0;
  color: var(--cl-label-85);
}

.loop-info-link {
  cursor: pointer;
}

.loop-info-handle {
  color: var(--cl-label-3);
  margin-left: 4px;
}

.loop-info-id {
  font-family: var(--cl-font-mono);
}
</style>
